'use client';

import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { List } from '@jouzen/ecom-components/v3/List';
import { ListItem } from '@jouzen/ecom-components/v3/ListItem';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import { Table, TableBody, TableCell, TableRow } from '@/app/components/Table';

import { declarationOfConformityGen4Data } from './_data/declarationOfConformityGen4Data';

export interface DeclarationOfConformityGen4 {
  equipment: MessageKey;
  modelType: MessageKey;
  productImage: string;
  directives: MessageKey[];
  technicalSpecifications: {
    title: MessageKey;
    description: string[];
  }[];
  signedBy: {
    date: MessageKey;
    signatureImage: string;
    name: string;
    position: MessageKey;
  };
}

const Content = () => {
  const t = useTranslations();

  return (
    <section data-cy="declaration-of-conformity-oura-ring-4">
      <LayoutGrid>
        {declarationOfConformityGen4Data.map((data, index) => (
          <GridItem
            key={data.equipment}
            colStart={{ sm: 'main' }}
            colEnd={{ sm: 'main' }}
            data-cy="declaration-of-conformity-oura-ring-4-content"
          >
            {/* Only one h1 can exist on the page, top most title will be the h1 */}
            <Typography
              variant="h2"
              Element={index === 0 ? 'h1' : 'h2'}
              className="mb-8"
            >
              {t('eu_declaration_of_conformity')}
            </Typography>
            <Typography weight="bold" className="mb-2">
              {t('eu_declaration_we')}
            </Typography>
            <Typography className="mb-2">Oura Health Oy</Typography>
            <Typography className="mb-2">Elektroniikkatie 10</Typography>
            <Typography className="mb-2">90590 Oulu</Typography>
            <Typography className="mb-8">Finland</Typography>
            <Typography weight="bold" className="mb-2">
              {t('eu_declaration_declare_products')}
            </Typography>
            <Typography className="mb-2">{t(data.equipment)}</Typography>
            <Typography className="mb-2">
              {t('eu_declaration_brand')}
            </Typography>
            <Typography className="mb-8">{t(data.modelType)}</Typography>
            <Image
              className="mb-8"
              src={data.productImage}
              format="png"
              alt="Gucci oura ring charger"
              width={500}
              height={500}
            />
            <Typography weight="bold" className="mb-2">
              {t('eu_declaration_conformity_with')}
            </Typography>
            {data.directives.map((directive) => (
              <Typography key={directive} className="mb-2">
                <RichText messageKey={directive} />
              </Typography>
            ))}
            <Typography weight="bold" className="mb-4">
              {t('eu_declaration_following_standards')}
            </Typography>

            <Table className="mb-10 text-sandstone-500">
              <TableBody>
                {data.technicalSpecifications.map((spec) => (
                  <TableRow key={spec.title}>
                    <TableCell>{t(spec.title)}</TableCell>
                    <TableCell>
                      <List Element="ul" condensed unstyled>
                        {spec.description.map((desc, i) => (
                          <ListItem key={i}>{desc}</ListItem>
                        ))}
                      </List>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography className="mb-2">{t(data.signedBy.date)}</Typography>
            <Image
              className="mb-2"
              src={data.signedBy.signatureImage}
              format="png"
              alt="Gucci oura ring charger"
              width={300}
              height={150}
            />
            <Typography className="mb-2">{data.signedBy.name}</Typography>
            <Typography>{t(data.signedBy.position)}</Typography>
            {index !== declarationOfConformityGen4Data.length - 1 && (
              <hr className="mx-auto my-20 max-w-3xl" />
            )}
          </GridItem>
        ))}
      </LayoutGrid>
    </section>
  );
};

export default Content;
